import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  TrendingDown,
  ShieldCheck,
  Banknote,
  Target,
  LineChart,
  SlidersHorizontal,
  Percent,
  GitCompare,
  Lightbulb,
  Calculator,
  FolderLock,
  Users,
  Lock,
  Layers,
  Bell,
  Settings as SettingsIcon,
  Moon,
  LifeBuoy,
  Search,
  ChevronDown,
  BookOpen,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";

// A friendly manual for every part of the app. Each topic explains WHAT it is,
// HOW to use it (steps), plus a pro tip — grouped and searchable.
const TONES = {
  emerald: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  blue: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  rose: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  violet: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  amber: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  sky: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  teal: "bg-teal-500/15 text-teal-600 dark:text-teal-400",
  orange: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
  slate: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

// Left accent bar (shown on each card) per tone.
const BAR = {
  emerald: "bg-emerald-400",
  blue: "bg-blue-400",
  rose: "bg-rose-400",
  violet: "bg-violet-400",
  amber: "bg-amber-400",
  sky: "bg-sky-400",
  teal: "bg-teal-400",
  orange: "bg-orange-400",
  slate: "bg-slate-400",
};

// Group header gradient wash per tone.
const GROUP_GRAD = {
  emerald: "from-emerald-500/15 via-emerald-500/5 to-transparent",
  blue: "from-blue-500/15 via-blue-500/5 to-transparent",
  violet: "from-violet-500/15 via-violet-500/5 to-transparent",
  slate: "from-slate-500/15 via-slate-500/5 to-transparent",
};

const GROUPS = [
  {
    group: "Your wealth",
    icon: Wallet,
    tone: "emerald",
    blurb: "Track what you own, owe and earn.",
    topics: [
      {
        key: "dashboard",
        icon: LayoutDashboard,
        tone: "emerald",
        title: "Dashboard",
        summary: "Your live financial snapshot in one screen.",
        what: "The home screen pulls everything together: net worth, a health score, your 10-year wealth projection, allocation, EMI deadlines and passive income — all recalculated as you add data.",
        how: [
          "Open the app to land here, or tap Overview in the sidebar.",
          "Scan the net-worth hero and health gauge for the big picture.",
          "Scroll for projections, allocation, EMIs and monthly income.",
        ],
        tip: "The numbers are only as good as your inputs — the more you add under Assets, Liabilities and Insurance, the sharper every projection becomes.",
      },
      {
        key: "assets",
        icon: Wallet,
        tone: "emerald",
        title: "Assets",
        summary: "Everything you own — from funds to gold to property.",
        what: "Track each holding with its value, expected return and (where relevant) quantity, SIP or tenure. AccessWealth uses these to compute net worth and growth.",
        how: [
          "Assets → Add asset, pick a type, and only the fields it needs appear.",
          "Enter the current value; add purchase value/date for gains tracking.",
          "Have many? Use Bulk add to enter several at once.",
        ],
        tip: "Set a realistic Expected Return per asset — it drives your projections. Bank and cash can be marked 'liquid' for a clearer emergency-fund view.",
      },
      {
        key: "mis",
        icon: Banknote,
        tone: "emerald",
        title: "Monthly Income Scheme",
        summary: "Assets that pay you a fixed monthly income.",
        what: "A special asset type (e.g. Post Office MIS) that pays regular income. The dashboard 'Monthly income' card totals what all your schemes pay each month.",
        how: [
          "Assets → Add asset → choose Monthly Income Scheme.",
          "Enter the amount invested and the interest rate p.a.",
          "See the estimated monthly income instantly, and on the dashboard.",
        ],
        tip: "Monthly income is estimated as value × rate ÷ 12 — perfect for planning how much passive income covers your expenses.",
      },
      {
        key: "liabilities",
        icon: TrendingDown,
        tone: "rose",
        title: "Liabilities",
        summary: "Loans and debts, with EMIs and payoff tracking.",
        what: "Record home/car/personal loans, credit cards and more. AccessWealth tracks outstanding balances, EMIs and models how debt shrinks over time.",
        how: [
          "Liabilities → Add liability and pick the loan type.",
          "Enter outstanding amount, rate, EMI and EMI day.",
          "When a loan is cleared, set its status to Closed / Paid off.",
        ],
        tip: "Closed liabilities stay in your list for history but stop counting toward totals and EMIs — so your numbers stay honest.",
      },
      {
        key: "insurance",
        icon: ShieldCheck,
        tone: "sky",
        title: "Insurance",
        summary: "Cover, premiums and cash value in one place.",
        what: "Track life, health and other policies — sum assured, premium and (for investment plans) cash/maturity value. Premium analysis shows how well you're covered.",
        how: [
          "Insurance → Add policy and pick the policy type.",
          "Enter insurer, sum assured, premium and frequency.",
          "Review the coverage analysis to spot gaps.",
        ],
        tip: "Lapsed / closed policies are excluded from your totals automatically — mark them so your cover figures reflect reality.",
      },
    ],
  },
  {
    group: "Planning & growth",
    icon: LineChart,
    tone: "blue",
    blurb: "Model your future and reach your goals.",
    topics: [
      {
        key: "goals",
        icon: Target,
        tone: "violet",
        title: "Goals",
        summary: "Turn dreams into funded, tracked targets.",
        what: "Define goals (retirement, education, a home…) with a target amount and date. AccessWealth inflation-adjusts them and shows if you're on track.",
        how: [
          "Goals → Add goal, set the target amount and target date.",
          "Pick a category so we can apply sensible inflation.",
          "Link assets to a goal to track funding progress.",
        ],
        tip: "Bulk add works here too — set up several goals in one go, grouped by type.",
      },
      {
        key: "projections",
        icon: LineChart,
        tone: "emerald",
        title: "Projections",
        summary: "See your wealth 10–30 years ahead.",
        what: "A forward look at assets, liabilities and net worth under different return scenarios — so you can see where today's choices lead.",
        how: [
          "Open Projections from the sidebar.",
          "Compare conservative, moderate and aggressive paths.",
          "Adjust Assumptions to see the lines move.",
        ],
        tip: "Real vs nominal matters — projections account for inflation so you see tomorrow's money in today's terms.",
      },
      {
        key: "assumptions",
        icon: SlidersHorizontal,
        tone: "blue",
        title: "Assumptions",
        summary: "The dials behind every projection.",
        what: "Global settings — inflation, per-class returns, retirement age and life expectancy — that power all forecasts. Change them once, everything updates.",
        how: [
          "Open Assumptions and tune inflation & expected returns.",
          "Set your retirement age and life expectancy.",
          "Save — projections, goals and scenarios recompute.",
        ],
        tip: "Be conservative with returns. It's better to be pleasantly surprised than to over-plan on optimistic numbers.",
      },
      {
        key: "taxation",
        icon: Percent,
        tone: "amber",
        title: "Taxation",
        summary: "Capital gains and tax-harvesting, simplified.",
        what: "Estimates short/long-term capital gains on your holdings and highlights harvesting opportunities to reduce tax.",
        how: [
          "Open Taxation to see gains across your portfolio.",
          "Enter or pick a sale date to model a disposal.",
          "Review harvesting suggestions before year-end.",
        ],
        tip: "Holding period drives the tax rate — the tool shows when an asset crosses from short to long term.",
      },
      {
        key: "scenarios",
        icon: GitCompare,
        tone: "violet",
        title: "Scenarios",
        summary: "What-if analysis and Monte Carlo odds.",
        what: "Model life events (a new goal, a windfall, selling an asset) and run Monte Carlo to see the probability your plan succeeds.",
        how: [
          "Open Scenarios and add what-if changes.",
          "Compare the scenario against your baseline.",
          "Run Monte Carlo for a success-probability read.",
        ],
        tip: "A plan with ~80%+ Monte Carlo success is generally healthy — below that, revisit savings or timelines.",
      },
      {
        key: "insights",
        icon: Lightbulb,
        tone: "amber",
        title: "Insights",
        summary: "Personalized, plain-English recommendations.",
        what: "Automatic observations about your portfolio — concentration, liquidity, diversification and more — with suggestions.",
        how: [
          "Open Insights any time for a fresh read.",
          "Act on the flagged items that matter most to you.",
        ],
        tip: "Insights update as your data changes — check back after big additions.",
      },
      {
        key: "calculators",
        icon: Calculator,
        tone: "teal",
        title: "Calculators",
        summary: "Quick financial calculators for everyday questions.",
        what: "SIP, EMI, retirement, goal and more — standalone tools for fast answers without touching your portfolio.",
        how: [
          "Open Calculators and pick one.",
          "Enter the inputs; results update live.",
          "Save a run to revisit later.",
        ],
        tip: "Great for 'what if I invest ₹X/month' gut-checks before committing.",
      },
    ],
  },
  {
    group: "Family & documents",
    icon: Users,
    tone: "violet",
    blurb: "Share, protect and pass on your wealth.",
    topics: [
      {
        key: "documents",
        icon: FolderLock,
        tone: "orange",
        title: "Documents",
        summary: "A secure home for important files.",
        what: "Store financial documents in encrypted folders and keep everything organized and accessible.",
        how: [
          "Open Documents and create folders.",
          "Upload files into the right folder.",
          "Share securely when needed.",
        ],
        tip: "Keep policy PDFs and nominee details here so your family can find them when it matters.",
      },
      {
        key: "family-sharing",
        icon: Users,
        tone: "blue",
        title: "Family Sharing",
        summary: "Share your finances with people you trust.",
        what: "Give family members a view of your finances so everyone stays on the same page — you control who sees what.",
        how: [
          "Family Sharing → invite by email.",
          "Choose what to share.",
          "Revoke access anytime.",
        ],
        tip: "Perfect for couples planning together, or keeping parents in the loop.",
      },
      {
        key: "family-vault",
        icon: Lock,
        tone: "emerald",
        title: "Family Vault",
        summary: "Nominees and inheritance allocation.",
        what: "Assign assets and insurance to nominees, so wealth distribution is clear and documented for the people you care about.",
        how: [
          "Family Vault → Add nominee with their details.",
          "Allocate one or more assets/policies to them.",
          "Review the allocation summary per nominee.",
        ],
        tip: "You can allocate several assets to one nominee in a single step — no need to add them one at a time.",
      },
      {
        key: "advisor-access",
        icon: ShieldCheck,
        tone: "emerald",
        title: "Advisor Access",
        summary: "Work with a Personal Finance Advisor.",
        what: "Connect an advisor with their code, choose exactly what they can see, and approve any changes they propose — nothing changes without your OK.",
        how: [
          "Advisor Access → enter your advisor's code.",
          "Pick modules to share and view or suggest permission.",
          "Approve or reject their change requests as they come in.",
        ],
        tip: "Every proposed change shows the exact details (amounts, dates) so you approve with full context.",
      },
    ],
  },
  {
    group: "Account & app",
    icon: SettingsIcon,
    tone: "slate",
    blurb: "Make the app yours and get help fast.",
    topics: [
      {
        key: "bulk",
        icon: Layers,
        tone: "emerald",
        title: "Bulk add",
        summary: "Add many records at once, grouped by type.",
        what: "A fast lane for entering lots of assets, liabilities, insurance or goals — pick a type, add several entries, add more types, then review before saving.",
        how: [
          "On Assets/Liabilities/Insurance/Goals, tap Bulk add.",
          "Choose a type and add multiple entries under it.",
          "Add more types, Review, then Confirm & add.",
        ],
        tip: "The Review step lets you edit any single entry or all of them before anything is saved.",
      },
      {
        key: "notifications",
        icon: Bell,
        tone: "amber",
        title: "Notifications",
        summary: "Alerts, approvals and reminders.",
        what: "The bell surfaces unread alerts — advisor requests, EMI reminders, messages and more. The full history lives on the Notifications page.",
        how: [
          "Tap the bell for what's new; tap an item to open it.",
          "Open Notifications (next to Settings) for everything.",
          "Filter by All / Unread / Read and by type.",
        ],
        tip: "EMIs re-surface with a reminder 10 days before they're due — so you never miss one.",
      },
      {
        key: "settings",
        icon: SettingsIcon,
        tone: "slate",
        title: "Settings",
        summary: "Your account, plan, security and data.",
        what: "Manage your profile and phone, upgrade your plan, change your password, export your data, and reach this guide.",
        how: [
          "Open Settings from your name or the menu.",
          "Update profile, security and appearance.",
          "Export your data or manage your subscription.",
        ],
        tip: "You can export a copy of your data anytime — it's your information.",
      },
      {
        key: "appearance",
        icon: Moon,
        tone: "violet",
        title: "Change appearance",
        summary: "Light or dark — your call.",
        what: "Switch the whole app between light and dark themes for comfortable viewing day or night.",
        how: [
          "Settings → Change appearance, or the theme toggle in the menu.",
          "Pick light or dark; it applies instantly everywhere.",
        ],
        tip: "Dark mode is easy on the eyes at night and looks great with the charts.",
      },
      {
        key: "support",
        icon: LifeBuoy,
        tone: "sky",
        title: "Support",
        summary: "Get help and raise tickets.",
        what: "Reach the team with a question or issue, and track the conversation until it's resolved.",
        how: [
          "Open Support and start a ticket.",
          "Describe your question with any detail.",
          "Get replies as notifications and continue the thread.",
        ],
        tip: "Include what you expected vs what happened — it gets you a faster, better answer.",
      },
    ],
  },
];

const ALL = GROUPS.flatMap((g) => g.topics.map((t) => ({ ...t, group: g.group })));

export default function Guide() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(() => new Set());

  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return null;
    return ALL.filter((t) =>
      [t.title, t.summary, t.what, t.tip, ...(t.how || [])]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [q]);

  return (
    <DashboardLayout>
      {/* HERO */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/15 via-blue-500/8 to-transparent" />
        <div className="relative p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-emerald-600" />
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Guide & tips
            </span>
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
            Everything, in plain English
          </h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
            A short manual for every part of AccessWealth — what it does, how to
            use it, and a pro tip. Tap any card to expand.
          </p>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 px-3 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
              <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
              {ALL.length} topics
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 px-3 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
              {GROUPS.length} sections
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 px-3 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
              ~2 min each
            </span>
          </div>

          {/* Search */}
          <div className="mt-5 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the guide…"
              className="input-soft !pl-10 bg-white/80 dark:bg-slate-900/60"
            />
          </div>
        </div>
      </div>

      {/* Category quick-jump (hidden while searching) */}
      {!q && (
        <div className="mb-6 flex flex-wrap gap-2">
          {GROUPS.map((g) => (
            <a
              key={g.group}
              href={`#${slug(g.group)}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-emerald-400 hover:text-emerald-600 transition"
            >
              {g.group}
              <span className="text-slate-400">{g.topics.length}</span>
            </a>
          ))}
        </div>
      )}

      {q ? (
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="card-soft p-10 text-center">
              <Search className="mx-auto h-7 w-7 text-slate-300" />
              <p className="mt-2 text-sm text-slate-500">
                Nothing matches “{query}”. Try another word.
              </p>
            </div>
          ) : (
            filtered.map((t) => (
              <TopicCard
                key={t.key}
                topic={t}
                isOpen={open.has(t.key)}
                onToggle={() => toggle(t.key)}
              />
            ))
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {GROUPS.map((g) => {
            const GIcon = g.icon;
            return (
              <section key={g.group} id={slug(g.group)} className="scroll-mt-20">
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 mb-3">
                  <div
                    className={`absolute inset-0 bg-gradient-to-r ${GROUP_GRAD[g.tone] || GROUP_GRAD.slate}`}
                  />
                  <div className="relative flex items-center gap-3 px-4 py-3">
                    <span
                      className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${TONES[g.tone] || TONES.slate}`}
                    >
                      <GIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-base font-bold text-slate-900 dark:text-white">
                        {g.group}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {g.blurb}
                      </div>
                    </div>
                    <span className="shrink-0 grid h-7 min-w-7 px-2 place-items-center rounded-full bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300">
                      {g.topics.length}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  {g.topics.map((t) => (
                    <TopicCard
                      key={t.key}
                      topic={t}
                      isOpen={open.has(t.key)}
                      onToggle={() => toggle(t.key)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <div className="mt-8 flex items-center justify-center">
        <Link
          to="/dashboard/settings"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-emerald-600"
        >
          Back to Settings <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="h-8" />
    </DashboardLayout>
  );
}

function TopicCard({ topic, isOpen, onToggle }) {
  const Icon = topic.icon;
  return (
    <div
      className={`group relative overflow-hidden card-soft transition-all ${
        isOpen
          ? "shadow-md ring-1 ring-slate-200 dark:ring-slate-700"
          : "hover:shadow-md hover:-translate-y-0.5"
      }`}
    >
      {/* coloured left accent */}
      <span
        className={`absolute left-0 inset-y-0 w-1.5 ${BAR[topic.tone] || BAR.slate}`}
      />
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 sm:p-5 pl-6 text-left"
      >
        <span
          className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${TONES[topic.tone] || TONES.slate}`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-bold text-slate-900 dark:text-white">
            {topic.title}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {topic.summary}
          </div>
        </div>
        <span
          className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border transition ${
            isOpen
              ? "border-emerald-300 text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10"
              : "border-slate-200 dark:border-slate-700 text-slate-400 group-hover:border-emerald-300 group-hover:text-emerald-500"
          }`}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {isOpen && (
        <div className="px-4 sm:px-5 pb-5 pl-6 -mt-1">
          <div className="sm:pl-[3.75rem] space-y-4">
            <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              {topic.what}
            </p>

            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                How to use it
              </div>
              <ol className="relative space-y-3 pl-1">
                {/* connecting line */}
                <span className="absolute left-[9px] top-2 bottom-2 w-px bg-slate-200 dark:bg-slate-700" />
                {topic.how.map((step, i) => (
                  <li key={i} className="relative flex items-start gap-3">
                    <span
                      className={`relative z-10 mt-0.5 grid h-[19px] w-[19px] shrink-0 place-items-center rounded-full text-[11px] font-bold ring-4 ring-white dark:ring-slate-900 ${TONES[topic.tone] || TONES.slate}`}
                    >
                      {i + 1}
                    </span>
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            {topic.tip && (
              <div className="flex items-start gap-2.5 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200/70 dark:border-amber-500/20 px-3.5 py-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-amber-400/20">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                </span>
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <span className="font-semibold">Pro tip · </span>
                  {topic.tip}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}
